import React, { useEffect, useState } from 'react';
import { Html } from '@react-three/drei';
import { Vector3 } from 'three';
import { useThree } from '@react-three/fiber';

interface IntersectionDialogProps {
    position: Vector3;
    visible: boolean;
    onYes: () => void;
    onNo: () => void;
}

export const IntersectionDialog: React.FC<IntersectionDialogProps> = ({
    position,
    visible,
    onYes,
    onNo
}) => {
    const { camera, size } = useThree();
    const [screenPos, setScreenPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    useEffect(() => {
        if (visible && position) {
            // Project 3D position to screen space
            const vector = position.clone();
            vector.project(camera);

            // Convert to pixel coordinates
            const x = (vector.x + 1) * size.width / 2;
            const y = (-vector.y + 1) * size.height / 2;

            // Ensure dialog stays within viewport bounds
            const dialogWidth = 200;
            const dialogHeight = 100;
            
            const boundedX = Math.max(10, Math.min(x - dialogWidth / 2, size.width - dialogWidth - 10));
            const boundedY = Math.max(10, Math.min(y - dialogHeight - 10, size.height - dialogHeight - 10));

            setScreenPos({ x: boundedX, y: boundedY });
        }
    }, [position, camera, size, visible]);

    if (!visible) return null;

    return (
        <Html
            position={[position.x, position.y, position.z]}
            style={{
                transform: `translate3d(${screenPos.x}px, ${screenPos.y}px, 0)`,
                transformStyle: 'preserve-3d'
            }}
            center
            occlude
            zIndexRange={[0, 10]}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 min-w-[200px] transform -translate-y-full -mt-2">
                <div className="text-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Create node at intersection point?
                    </h3>
                </div>
                <div className="flex justify-center space-x-3">
                    <button
                        onClick={onNo}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                        No
                    </button>
                    <button
                        onClick={onYes}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        Yes
                    </button>
                </div>
            </div>
        </Html>
    );
};
